import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { FiChevronRight } from "react-icons/fi";
import { Link, useNavigate } from 'react-router-dom';
import { BaseInput, BaseSelect, BaseButton } from "../../components/ui";
import inmuebleService from "../../services/inmuebleService";
import espacioService from "../../services/espacioService";
import pisoService from "../../services/pisoService";
import Swal from 'sweetalert2';
import { useAuth } from "../../utils/AuthContext";

const TIPOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia Bancaria' },
  { value: 'paypal', label: 'Paypal' },
  { value: 'tarjeta', label: 'Tarjeta de Crédito/Débito' },
  { value: 'yape', label: 'Yape' },
  { value: 'plin', label: 'Plin' },
];

const InquilinosPago = () => {
  const { estaAutenticado } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [inmuebles, setInmuebles] = useState([]);
  const [pisos, setPisos] = useState([]);
  const [espacios, setEspacios] = useState([]);
  const [formData, setFormData] = useState({
    inmuebleId: '',
    pisoId: '',
    espacioId: '',
    dni: '',
    nombre: '',
    fechaPago: '',
    tipoPago: '',
    monto: '',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const cargarInmuebles = async () => {
      try {
        const data = await inmuebleService.obtenerInmuebles();
        if (data && Array.isArray(data)) {
          setInmuebles(data);
        }
      } catch (error) {
        console.error('Error al cargar inmuebles:', error);
      }
    };

    cargarInmuebles();
  }, []);

  useEffect(() => {
    const cargarPisos = async () => {
      if (formData.inmuebleId) {
        try {
          const data = await pisoService.obtenerPorInmueble(formData.inmuebleId);
          if (data && Array.isArray(data)) {
            setPisos(data);
          }
        } catch (error) {
          console.error('Error al cargar pisos:', error);
        }
      } else {
        setPisos([]);
        setEspacios([]);
      }
    };

    cargarPisos();
  }, [formData.inmuebleId]);

  useEffect(() => {
    const cargarEspacios = async () => {
      if (formData.pisoId) {
        try {
          const data = await espacioService.obtenerPorPiso(formData.pisoId);
          if (data && Array.isArray(data)) {
            setEspacios(data);
          }
        } catch (error) {
          console.error('Error al cargar espacios:', error);
        }
      } else {
        setEspacios([]);
      }
    };

    cargarEspacios();
  }, [formData.pisoId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'inmuebleId') {
      setFormData((prev) => ({
        ...prev,
        inmuebleId: value,
        pisoId: '',
        espacioId: '',
      }));
      setFormErrors((prev) => ({ ...prev, inmuebleId: '', pisoId: '', espacioId: '' }));
      return;
    }

    if (name === 'pisoId') {
      setFormData((prev) => ({ ...prev, pisoId: value, espacioId: '' }));
      setFormErrors((prev) => ({ ...prev, pisoId: '', espacioId: '' }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.inmuebleId) {
      errors.inmuebleId = 'Debe seleccionar un inmueble';
      isValid = false;
    }
    if (!formData.pisoId) {
      errors.pisoId = 'Debe seleccionar un piso';
      isValid = false;
    }
    if (!formData.espacioId) {
      errors.espacioId = 'Debe seleccionar un espacio';
      isValid = false;
    }
    if (!formData.dni.trim()) {
      errors.dni = 'El DNI es requerido';
      isValid = false;
    }
    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
      isValid = false;
    }
    if (!formData.fechaPago) {
      errors.fechaPago = 'La fecha de pago es requerida';
      isValid = false;
    }
    if (!formData.tipoPago) {
      errors.tipoPago = 'Debe seleccionar un tipo de pago';
      isValid = false;
    }
    if (!formData.monto.trim()) {
      errors.monto = 'El monto es requerido';
      isValid = false;
    } else if (isNaN(formData.monto) || parseFloat(formData.monto) <= 0) {
      errors.monto = 'El monto debe ser un número válido mayor a 0';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!estaAutenticado) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No hay sesión activa',
      });
      return;
    }

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Error de validación',
        text: 'Por favor complete todos los campos requeridos correctamente',
      });
      return;
    }

    try {
      setLoading(true);
      // await pagoService.registrarPago({...});

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'El pago se ha registrado correctamente',
        showConfirmButton: false,
        timer: 1500,
      }).then(() => {
        navigate('/inquilinos');
      });
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al registrar el pago',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, volver',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/inquilinos-registros');
      }
    });
  };

  const espacioOptions = espacios.map((e) => ({
    value: e.id,
    label: `${e.nombre} - ${e.tipo_espacio} (S/ ${parseFloat(e.precio).toFixed(2)})`,
  }));

  return (
    <>
      <Header />
      <Sidebar id="menu-item" id1="menu-items" activeClassName="inquilinos-pago" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Inquilinos</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FiChevronRight />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Registrar Pago</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Registrar pago de un inquilino</h4>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseSelect
                          label="Inmueble"
                          name="inmuebleId"
                          required
                          isSearchable
                          isClearable
                          value={formData.inmuebleId}
                          onChange={handleChange}
                          placeholder="Selecciona un inmueble"
                          error={formErrors.inmuebleId}
                          options={inmuebles.map((i) => ({ value: i.id, label: i.nombre }))}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseSelect
                          label="Piso"
                          name="pisoId"
                          required
                          isSearchable
                          isClearable
                          value={formData.pisoId}
                          onChange={handleChange}
                          disabled={!formData.inmuebleId}
                          placeholder="Selecciona un piso"
                          error={formErrors.pisoId}
                          options={pisos.map((p) => ({ value: p.id, label: p.nombre }))}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseSelect
                          label="Espacio"
                          name="espacioId"
                          required
                          isSearchable
                          isClearable
                          value={formData.espacioId}
                          onChange={handleChange}
                          disabled={!formData.pisoId}
                          placeholder="Selecciona un espacio"
                          error={formErrors.espacioId}
                          options={espacioOptions}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseInput
                          label="DNI del inquilino"
                          name="dni"
                          required
                          restrict="number"
                          maxLength={12}
                          value={formData.dni}
                          onChange={handleChange}
                          error={formErrors.dni}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseInput
                          label="Nombre del inquilino"
                          name="nombre"
                          required
                          value={formData.nombre}
                          onChange={handleChange}
                          error={formErrors.nombre}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseInput
                          label="Fecha de Pago"
                          name="fechaPago"
                          type="date"
                          required
                          value={formData.fechaPago}
                          onChange={handleChange}
                          error={formErrors.fechaPago}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseSelect
                          label="Tipo de Pago"
                          name="tipoPago"
                          required
                          isClearable
                          value={formData.tipoPago}
                          onChange={handleChange}
                          placeholder="Selecciona el tipo de pago"
                          error={formErrors.tipoPago}
                          options={TIPOS_PAGO}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-xl-6">
                        <BaseInput
                          label="Monto pagado"
                          name="monto"
                          required
                          restrict="decimal"
                          value={formData.monto}
                          onChange={handleChange}
                          placeholder="0.00"
                          startIcon={<span>S/</span>}
                          error={formErrors.monto}
                        />
                      </div>

                      <div className="col-12">
                        <div className="doctor-submit text-end d-flex justify-content-end gap-2">
                          <BaseButton
                            type="submit"
                            variant="primary"
                            loading={loading}
                            disabled={loading}
                          >
                            Guardar
                          </BaseButton>
                          <BaseButton
                            type="button"
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            Cancelar
                          </BaseButton>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InquilinosPago;
